import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useState } from "react";
import type {
  AdminLessonsResponseDto,
  AdminMembersResponseDto,
  CreateAdminLessonRequestDto,
  MemberRole,
  RecurrencePreset,
  StudiosResponseDto
} from "@madamuh/domain";
import { getRecurrencePresetLabel } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers, fetchAdminStudios } from "../../shared/api/adminDirectory";
import { createAdminLesson, fetchAdminLessons } from "../../shared/api/adminLesson";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { GENERIC_TENANT_LABELS, getTenantLabels, type TenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

function buildAdminLinks(labels: TenantLabels) {
  return [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/recurring-slots", label: labels.studio }
];
}

const WEEKDAY_OPTIONS = [
  { weekday: 0, label: "日" },
  { weekday: 1, label: "月" },
  { weekday: 2, label: "火" },
  { weekday: 3, label: "水" },
  { weekday: 4, label: "木" },
  { weekday: 5, label: "金" },
  { weekday: 6, label: "土" }
] as const;

const RECURRENCE_OPTIONS: RecurrencePreset[] = [
  "weekly",
  "monthlyWeeks1To4",
  "monthlyWeeks1And3",
  "monthlyWeeks2And4",
  "monthlyWeekOfStartDate",
  "irregular"
];

type WeekdayValue = (typeof WEEKDAY_OPTIONS)[number]["weekday"];
type WeekdaySlotMap = Record<WeekdayValue, WeekdaySlotFormState>;

function createWeekdaySlotState(
  startTime: string,
  endTime: string,
  studioId: string,
  studioTbd: boolean,
  teacherPlatformUserIds: readonly string[],
  teacherTbd: boolean
): WeekdaySlotFormState {
  return {
    enabled: false,
    startTime,
    endTime,
    studioId,
    studioTbd,
    teacherPlatformUserIds: [...teacherPlatformUserIds],
    teacherTbd,
    syncStartTime: true,
    syncEndTime: true,
    syncStudio: true,
    syncTeachers: true
  };
}

function createInitialWeekdaySlots(): WeekdaySlotMap {
  return Object.fromEntries(
    WEEKDAY_OPTIONS.map(({ weekday }) => [weekday, createWeekdaySlotState("17:00", "18:00", "", false, [], false)])
  ) as WeekdaySlotMap;
}

type RegularLessonFormState = {
  title: string;
  shortTitle: string;
  description: string;
  genre: string;
  targetLabel: string;
  startDate: string;
  defaultStartTime: string;
  defaultEndTime: string;
  defaultStudioId: string;
  defaultStudioTbd: boolean;
  defaultTeacherPlatformUserIds: string[];
  defaultTeacherTbd: boolean;
  recurrencePreset: RecurrencePreset;
  publicVisible: boolean;
  trialPolicyId: string;
  visibilityPolicyId: string;
  weekdaySlots: WeekdaySlotMap;
};

type WeekdaySlotFormState = {
  enabled: boolean;
  startTime: string;
  endTime: string;
  studioId: string;
  studioTbd: boolean;
  teacherPlatformUserIds: string[];
  teacherTbd: boolean;
  syncStartTime: boolean;
  syncEndTime: boolean;
  syncStudio: boolean;
  syncTeachers: boolean;
};

type ShortTermLessonFormState = {
  title: string;
  shortTitle: string;
  description: string;
  genre: string;
  targetLabel: string;
  firstOccurrenceDate: string;
  lastOccurrenceDate: string;
  startTime: string;
  endTime: string;
  studioId: string;
  studioTbd: boolean;
  teacherPlatformUserIds: string[];
  teacherTbd: boolean;
  publicVisible: boolean;
};

function createInitialRegularLessonFormState(): RegularLessonFormState {
  return {
    title: "",
    shortTitle: "",
    description: "",
    genre: "",
    targetLabel: "",
    startDate: "",
    defaultStartTime: "17:00",
    defaultEndTime: "18:00",
    defaultStudioId: "",
    defaultStudioTbd: false,
    defaultTeacherPlatformUserIds: [],
    defaultTeacherTbd: false,
    recurrencePreset: "weekly",
    publicVisible: true,
    trialPolicyId: "",
    visibilityPolicyId: "",
    weekdaySlots: createInitialWeekdaySlots()
  };
}

function createInitialShortTermLessonFormState(): ShortTermLessonFormState {
  return {
    title: "",
    shortTitle: "",
    description: "",
    genre: "",
    targetLabel: "",
    firstOccurrenceDate: "",
    lastOccurrenceDate: "",
    startTime: "10:00",
    endTime: "11:30",
    studioId: "",
    studioTbd: false,
    teacherPlatformUserIds: [],
    teacherTbd: false,
    publicVisible: true
  };
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function hasTeacherRole(roles: readonly MemberRole[]): boolean {
  return roles.includes("teacher") || roles.includes("admin");
}

function toggleSelection(current: readonly string[], value: string, checked: boolean): string[] {
  const next = new Set(current);

  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }

  return [...next];
}

function copyWeekdaySlotWithCommonDefaults(
  slot: WeekdaySlotFormState,
  form: Pick<
    RegularLessonFormState,
    | "defaultStartTime"
    | "defaultEndTime"
    | "defaultStudioId"
    | "defaultStudioTbd"
    | "defaultTeacherPlatformUserIds"
    | "defaultTeacherTbd"
  >
): WeekdaySlotFormState {
  return {
    ...slot,
    startTime: slot.syncStartTime ? form.defaultStartTime : slot.startTime,
    endTime: slot.syncEndTime ? form.defaultEndTime : slot.endTime,
    studioId: slot.syncStudio ? form.defaultStudioId : slot.studioId,
    studioTbd: slot.syncStudio ? form.defaultStudioTbd : slot.studioTbd,
    teacherPlatformUserIds: slot.syncTeachers
      ? [...form.defaultTeacherPlatformUserIds]
      : slot.teacherPlatformUserIds,
    teacherTbd: slot.syncTeachers ? form.defaultTeacherTbd : slot.teacherTbd
  };
}

function getEnabledWeekdayCount(weekdaySlots: WeekdaySlotMap): number {
  return Object.values(weekdaySlots).filter((slot) => slot.enabled).length;
}

function buildRegularCreateRequest(form: RegularLessonFormState): CreateAdminLessonRequestDto {
  const hasDefaultStudio = form.defaultStudioId.trim().length > 0;
  const hasDefaultTeachers = form.defaultTeacherPlatformUserIds.length > 0;
  const defaultStudioDecisionStatus = form.defaultStudioTbd || !hasDefaultStudio ? "tbd" : "confirmed";
  const defaultTeacherAssignmentStatus = form.defaultTeacherTbd || !hasDefaultTeachers ? "tbd" : "assigned";
  const weekdaySlots =
    form.recurrencePreset === "irregular"
      ? []
      : WEEKDAY_OPTIONS.filter(({ weekday }) => form.weekdaySlots[weekday]?.enabled).map(({ weekday }) => {
          const slot = form.weekdaySlots[weekday];
          const studioDecisionStatus = slot.studioTbd ? "tbd" as const : "confirmed" as const;
          const teacherAssignmentStatus = slot.teacherTbd ? "tbd" as const : "assigned" as const;

          return {
            weekday,
            startTime: slot.startTime,
            endTime: slot.endTime,
            studioId: slot.studioTbd ? null : slot.studioId || null,
            studioDecisionStatus,
            teacherPlatformUserIds: slot.teacherTbd ? [] : [...slot.teacherPlatformUserIds],
            teacherAssignmentStatus
          };
        });

  return {
    title: form.title.trim(),
    shortTitle: form.shortTitle.trim() || null,
    description: form.description.trim() || null,
    genre: form.genre.trim() || null,
    targetLabel: form.targetLabel.trim() || null,
    lessonKind: "regular",
    publicVisible: form.publicVisible,
    trialPolicyId: form.trialPolicyId.trim() || null,
    visibilityPolicyId: form.visibilityPolicyId.trim() || null,
    startDate: form.startDate.trim() || null,
    defaultStartTime: form.defaultStartTime,
    defaultEndTime: form.defaultEndTime,
    startTime: form.defaultStartTime,
    endTime: form.defaultEndTime,
    defaultStudioId: defaultStudioDecisionStatus === "tbd" ? null : form.defaultStudioId || null,
    studioId: defaultStudioDecisionStatus === "tbd" ? null : form.defaultStudioId || null,
    defaultStudioDecisionStatus,
    studioTbd: defaultStudioDecisionStatus === "tbd",
    defaultTeacherPlatformUserIds:
      defaultTeacherAssignmentStatus === "tbd" ? [] : [...form.defaultTeacherPlatformUserIds],
    teacherPlatformUserIds:
      defaultTeacherAssignmentStatus === "tbd" ? [] : [...form.defaultTeacherPlatformUserIds],
    defaultTeacherAssignmentStatus,
    teacherTbd: defaultTeacherAssignmentStatus === "tbd",
    recurrencePreset: form.recurrencePreset,
    weekdaySlots
  };
}

function buildShortTermCreateRequest(form: ShortTermLessonFormState): CreateAdminLessonRequestDto {
  return {
    title: form.title.trim(),
    shortTitle: form.shortTitle.trim() || null,
    description: form.description.trim() || null,
    genre: form.genre.trim() || null,
    targetLabel: form.targetLabel.trim() || null,
    lessonKind: "shortTerm",
    publicVisible: form.publicVisible,
    trialPolicyId: null,
    visibilityPolicyId: null,
    startTime: form.startTime,
    endTime: form.endTime,
    studioId: form.studioTbd ? null : form.studioId || null,
    studioTbd: form.studioTbd,
    teacherPlatformUserIds: form.teacherTbd ? [] : [...form.teacherPlatformUserIds],
    teacherTbd: form.teacherTbd,
    firstOccurrenceDate: form.firstOccurrenceDate.trim() || null,
    lastOccurrenceDate: form.lastOccurrenceDate.trim() || null
  };
}

export function AdminLessonsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [lessons, setLessons] = useState<AdminLessonsResponseDto | null>(null);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [studios, setStudios] = useState<StudiosResponseDto | null>(null);
  const [regularForm, setRegularForm] = useState<RegularLessonFormState>(createInitialRegularLessonFormState());
  const [shortTermForm, setShortTermForm] = useState<ShortTermLessonFormState>(createInitialShortTermLessonFormState());
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const teacherMembers = members?.items.filter((member) => hasTeacherRole(member.roles)) ?? [];

  useEffect(() => {
    setRegularForm((current) => ({
      ...current,
      weekdaySlots: Object.fromEntries(
        WEEKDAY_OPTIONS.map(({ weekday }) => [
          weekday,
          copyWeekdaySlotWithCommonDefaults(current.weekdaySlots[weekday], current)
        ])
      ) as WeekdaySlotMap
    }));
  }, [
    regularForm.defaultStartTime,
    regularForm.defaultEndTime,
    regularForm.defaultStudioId,
    regularForm.defaultStudioTbd,
    regularForm.defaultTeacherPlatformUserIds,
    regularForm.defaultTeacherTbd
  ]);

  function updateCommonDefaults(
    updater: (current: RegularLessonFormState) => Partial<RegularLessonFormState>
  ) {
    setRegularForm((current) => ({
      ...current,
      ...updater(current)
    }));
  }

  function updateWeekdaySlot(
    weekday: WeekdayValue,
    updater: (slot: WeekdaySlotFormState) => WeekdaySlotFormState
  ) {
    setRegularForm((current) => ({
      ...current,
      weekdaySlots: {
        ...current.weekdaySlots,
        [weekday]: updater(current.weekdaySlots[weekday])
      }
    }));
  }

  function handleWeekdayToggle(weekday: WeekdayValue, checked: boolean) {
    setRegularForm((current) => {
      const currentSlot = current.weekdaySlots[weekday];
      const nextSlot: WeekdaySlotFormState = checked
        ? {
            ...copyWeekdaySlotWithCommonDefaults(currentSlot, current),
            enabled: true
          }
        : {
            ...currentSlot,
            enabled: false
          };

      return {
        ...current,
        weekdaySlots: {
          ...current.weekdaySlots,
          [weekday]: nextSlot
        }
      };
    });
  }

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextLessons, nextMembers, nextStudios] = await Promise.all([
        fetchAdminLessons(tenantSlug),
        fetchAdminMembers(tenantSlug),
        fetchAdminStudios(tenantSlug)
      ]);

      setLessons(nextLessons);
      setMembers(nextMembers);
      setStudios(nextStudios);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.lesson}一覧の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  async function handleCreateRegularLesson(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-regular");
    setLoadError(null);

    try {
      await createAdminLesson(tenantSlug, buildRegularCreateRequest(regularForm));
      setRegularForm(createInitialRegularLessonFormState());
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `通常${tenantLabels.lesson}の作成に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCreateShortTermLesson(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-shortterm");
    setLoadError(null);

    try {
      await createAdminLesson(tenantSlug, buildShortTermCreateRequest(shortTermForm));
      setShortTermForm(createInitialShortTermLessonFormState());
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `短期${tenantLabels.lesson}の作成に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={tenantLabels.lesson}
      description={`通常${tenantLabels.lesson}と短期${tenantLabels.lesson}を作成し、スケジュール生成につなぐ画面です。`}
      links={buildAdminLinks(tenantLabels)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>{tenantLabels.lesson}・メンバー・{tenantLabels.studio}を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{tenantLabels.lesson}一覧</h2>
              {lessons?.items.length === 0 ? (
                <p style={{ margin: 0 }}>{tenantLabels.lesson}はまだありません。</p>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {lessons?.items.map((lesson) => (
                    <Link
                      key={lesson.lessonId}
                      to={`/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}`}
                      style={{
                        display: "grid",
                        gap: 6,
                        padding: 12,
                        border: "1px solid #e5e5e5",
                        borderRadius: 12,
                        color: "inherit",
                        textDecoration: "none"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                        <strong>{lesson.title}</strong>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>{describeStatus(lesson.lessonKind)}</span>
                      </div>
                      <div style={{ fontSize: 14, opacity: 0.8 }}>
                        {describeStatus(lesson.status)} / {lesson.startDate} 〜 {lesson.endDate} / 公開対象:{" "}
                        {lesson.publicVisible ? "公開" : "非公開"}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {lesson.defaultStudioName ?? `${tenantLabels.studio}未定`} /{" "}
                        {lesson.primaryTeacherNameSnapshots.join(", ") || `${tenantLabels.teacher}未定`}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section
              style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
            >
              <h2 style={{ margin: 0, fontSize: 18 }}>通常{tenantLabels.lesson}作成</h2>
              <form onSubmit={(event) => void handleCreateRegularLesson(event)} style={{ display: "grid", gap: 12 }}>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>{tenantLabels.lesson}名</span>
                  <input
                    value={regularForm.title}
                    onChange={(event) => setRegularForm((current) => ({ ...current, title: event.target.value }))}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>略称</span>
                  <input
                    value={regularForm.shortTitle}
                    onChange={(event) =>
                      setRegularForm((current) => ({ ...current, shortTitle: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>説明</span>
                  <textarea
                    value={regularForm.description}
                    onChange={(event) =>
                      setRegularForm((current) => ({ ...current, description: event.target.value }))
                    }
                    rows={3}
                  />
                </label>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>カテゴリ</span>
                    <input
                      value={regularForm.genre}
                      onChange={(event) => setRegularForm((current) => ({ ...current, genre: event.target.value }))}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>対象</span>
                    <input
                      value={regularForm.targetLabel}
                      onChange={(event) =>
                        setRegularForm((current) => ({ ...current, targetLabel: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>開始日</span>
                    <input
                      type="date"
                      value={regularForm.startDate}
                      onChange={(event) =>
                        setRegularForm((current) => ({ ...current, startDate: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>繰り返し</span>
                    <select
                      value={regularForm.recurrencePreset}
                      onChange={(event) =>
                        setRegularForm((current) => ({
                          ...current,
                          recurrencePreset: event.target.value as RecurrencePreset
                        }))
                      }
                    >
                      {RECURRENCE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {getRecurrencePresetLabel(option)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div style={{ display: "grid", gap: 12 }}>
                  <strong style={{ fontSize: 14 }}>共通デフォルト</strong>
                  <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>共通開始時間</span>
                      <input
                        type="time"
                        value={regularForm.defaultStartTime}
                        onChange={(event) =>
                          updateCommonDefaults(() => ({ defaultStartTime: event.target.value }))
                        }
                      />
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>共通終了時間</span>
                      <input
                        type="time"
                        value={regularForm.defaultEndTime}
                        onChange={(event) =>
                          updateCommonDefaults(() => ({ defaultEndTime: event.target.value }))
                        }
                      />
                    </label>
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={regularForm.defaultStudioTbd}
                        onChange={(event) =>
                          updateCommonDefaults(() => ({ defaultStudioTbd: event.target.checked }))
                        }
                      />
                      <span>共通{tenantLabels.studio}未定</span>
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>共通{tenantLabels.studio}</span>
                      <select
                        value={regularForm.defaultStudioId}
                        disabled={regularForm.defaultStudioTbd}
                        onChange={(event) =>
                          updateCommonDefaults(() => ({ defaultStudioId: event.target.value }))
                        }
                      >
                        <option value="">選択してください</option>
                        {studios?.items.map((studio) => (
                          <option key={studio.studioId} value={studio.studioId}>
                            {studio.displayName}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={regularForm.defaultTeacherTbd}
                        onChange={(event) =>
                          updateCommonDefaults(() => ({ defaultTeacherTbd: event.target.checked }))
                        }
                      />
                      <span>共通{tenantLabels.teacher}未定</span>
                    </label>
                    <div style={{ display: "grid", gap: 6 }}>
                      <strong style={{ fontSize: 14 }}>共通{tenantLabels.teacher}</strong>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {teacherMembers.map((member) => (
                          <label key={member.platformUserId} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              disabled={regularForm.defaultTeacherTbd}
                              checked={regularForm.defaultTeacherPlatformUserIds.includes(member.platformUserId)}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                updateCommonDefaults((current) => ({
                                  defaultTeacherPlatformUserIds: toggleSelection(
                                    current.defaultTeacherPlatformUserIds,
                                    member.platformUserId,
                                    event.target.checked
                                  )
                                }))
                              }
                            />
                            <span>{getMemberDisplayName(member)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {regularForm.recurrencePreset === "irregular" ? (
                  <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                    不定期{tenantLabels.lesson}は自動で予定を生成しません。{tenantLabels.lesson}詳細またはスケジュール画面から個別予定を追加してください。
                  </p>
                ) : (
                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "grid", gap: 6 }}>
                      <strong style={{ fontSize: 14 }}>曜日ごとの定期枠</strong>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {WEEKDAY_OPTIONS.map(({ weekday, label }) => (
                          <label key={weekday} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={regularForm.weekdaySlots[weekday].enabled}
                              onChange={(event) => handleWeekdayToggle(weekday, event.target.checked)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        選択中: {getEnabledWeekdayCount(regularForm.weekdaySlots)}件
                      </span>
                    </div>
                    <div style={{ display: "grid", gap: 12 }}>
                      {WEEKDAY_OPTIONS.filter(({ weekday }) => regularForm.weekdaySlots[weekday].enabled).map(
                        ({ weekday, label }) => {
                          const slot = regularForm.weekdaySlots[weekday];

                          return (
                            <article
                              key={weekday}
                              style={{
                                display: "grid",
                                gap: 10,
                                padding: 12,
                                border: "1px solid #e5e5e5",
                                borderRadius: 12
                              }}
                            >
                              <strong>{label}曜日</strong>
                              <div
                                style={{
                                  display: "grid",
                                  gap: 12,
                                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))"
                                }}
                              >
                                <label style={{ display: "grid", gap: 4 }}>
                                  <span>開始時刻</span>
                                  <input
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(event) =>
                                      updateWeekdaySlot(weekday, (current) => ({
                                        ...current,
                                        startTime: event.target.value,
                                        syncStartTime: false
                                      }))
                                    }
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4 }}>
                                  <span>終了時刻</span>
                                  <input
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(event) =>
                                      updateWeekdaySlot(weekday, (current) => ({
                                        ...current,
                                        endTime: event.target.value,
                                        syncEndTime: false
                                      }))
                                    }
                                  />
                                </label>
                              </div>
                              <div style={{ display: "grid", gap: 8 }}>
                                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={slot.studioTbd}
                                    onChange={(event) =>
                                      updateWeekdaySlot(weekday, (current) => ({
                                        ...current,
                                        studioTbd: event.target.checked,
                                        syncStudio: false
                                      }))
                                    }
                                  />
                                  <span>{tenantLabels.studio}未定</span>
                                </label>
                                <label style={{ display: "grid", gap: 4 }}>
                                  <span>{tenantLabels.studio}</span>
                                  <select
                                    value={slot.studioId}
                                    disabled={slot.studioTbd}
                                    onChange={(event) =>
                                      updateWeekdaySlot(weekday, (current) => ({
                                        ...current,
                                        studioId: event.target.value,
                                        syncStudio: false
                                      }))
                                    }
                                  >
                                    <option value="">選択してください</option>
                                    {studios?.items.map((studio) => (
                                      <option key={studio.studioId} value={studio.studioId}>
                                        {studio.displayName}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              <div style={{ display: "grid", gap: 8 }}>
                                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={slot.teacherTbd}
                                    onChange={(event) =>
                                      updateWeekdaySlot(weekday, (current) => ({
                                        ...current,
                                        teacherTbd: event.target.checked,
                                        syncTeachers: false
                                      }))
                                    }
                                  />
                                  <span>{tenantLabels.teacher}未定</span>
                                </label>
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                  {teacherMembers.map((member) => (
                                    <label
                                      key={`${weekday}:${member.platformUserId}`}
                                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                                    >
                                      <input
                                        type="checkbox"
                                        disabled={slot.teacherTbd}
                                        checked={slot.teacherPlatformUserIds.includes(member.platformUserId)}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                          updateWeekdaySlot(weekday, (current) => ({
                                            ...current,
                                            teacherPlatformUserIds: toggleSelection(
                                              current.teacherPlatformUserIds,
                                              member.platformUserId,
                                              event.target.checked
                                            ),
                                            syncTeachers: false
                                          }))
                                        }
                                      />
                                      <span>{getMemberDisplayName(member)}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </article>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>体験ポリシーID</span>
                    <input
                      value={regularForm.trialPolicyId}
                      onChange={(event) =>
                        setRegularForm((current) => ({ ...current, trialPolicyId: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>公開ポリシーID</span>
                    <input
                      value={regularForm.visibilityPolicyId}
                      onChange={(event) =>
                        setRegularForm((current) => ({ ...current, visibilityPolicyId: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={regularForm.publicVisible}
                    onChange={(event) =>
                      setRegularForm((current) => ({ ...current, publicVisible: event.target.checked }))
                    }
                  />
                  <span>公開対象</span>
                </label>
                <button type="submit" disabled={busyKey === "create-regular"}>
                  {busyKey === "create-regular" ? "作成中..." : `${tenantLabels.lesson}を作成`}
                </button>
              </form>
            </section>

            <section
              style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
            >
              <h2 style={{ margin: 0, fontSize: 18 }}>短期{tenantLabels.lesson}作成</h2>
              <form onSubmit={(event) => void handleCreateShortTermLesson(event)} style={{ display: "grid", gap: 12 }}>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>{tenantLabels.lesson}名</span>
                  <input
                    value={shortTermForm.title}
                    onChange={(event) => setShortTermForm((current) => ({ ...current, title: event.target.value }))}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>略称</span>
                  <input
                    value={shortTermForm.shortTitle}
                    onChange={(event) =>
                      setShortTermForm((current) => ({ ...current, shortTitle: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>説明</span>
                  <textarea
                    value={shortTermForm.description}
                    onChange={(event) =>
                      setShortTermForm((current) => ({ ...current, description: event.target.value }))
                    }
                    rows={3}
                  />
                </label>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>カテゴリ</span>
                    <input
                      value={shortTermForm.genre}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, genre: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>対象</span>
                    <input
                      value={shortTermForm.targetLabel}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, targetLabel: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>開始日</span>
                    <input
                      type="date"
                      value={shortTermForm.firstOccurrenceDate}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, firstOccurrenceDate: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>終了日</span>
                    <input
                      type="date"
                      value={shortTermForm.lastOccurrenceDate}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, lastOccurrenceDate: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>開始時間</span>
                    <input
                      type="time"
                      value={shortTermForm.startTime}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, startTime: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>終了時間</span>
                    <input
                      type="time"
                      value={shortTermForm.endTime}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, endTime: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={shortTermForm.studioTbd}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, studioTbd: event.target.checked }))
                      }
                    />
                    <span>{tenantLabels.studio}未定</span>
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>{tenantLabels.studio}</span>
                    <select
                      value={shortTermForm.studioId}
                      disabled={shortTermForm.studioTbd}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, studioId: event.target.value }))
                      }
                    >
                      <option value="">選択してください</option>
                      {studios?.items.map((studio) => (
                        <option key={studio.studioId} value={studio.studioId}>
                          {studio.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={shortTermForm.teacherTbd}
                      onChange={(event) =>
                        setShortTermForm((current) => ({ ...current, teacherTbd: event.target.checked }))
                      }
                    />
                    <span>{tenantLabels.teacher}未定</span>
                  </label>
                  <div style={{ display: "grid", gap: 6 }}>
                    <strong style={{ fontSize: 14 }}>{tenantLabels.teacher}</strong>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {teacherMembers.map((member) => (
                        <label key={member.platformUserId} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <input
                            type="checkbox"
                            disabled={shortTermForm.teacherTbd}
                            checked={shortTermForm.teacherPlatformUserIds.includes(member.platformUserId)}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              setShortTermForm((current) => ({
                                ...current,
                                teacherPlatformUserIds: toggleSelection(
                                  current.teacherPlatformUserIds,
                                  member.platformUserId,
                                  event.target.checked
                                )
                              }))
                            }
                          />
                          <span>{getMemberDisplayName(member)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={shortTermForm.publicVisible}
                    onChange={(event) =>
                      setShortTermForm((current) => ({ ...current, publicVisible: event.target.checked }))
                    }
                  />
                  <span>公開対象</span>
                </label>
                <button type="submit" disabled={busyKey === "create-shortterm"}>
                  {busyKey === "create-shortterm" ? "作成中..." : `${tenantLabels.lesson}を作成`}
                </button>
              </form>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
